import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

// SECURITY DECISION (documented per code review): localStorage here stores ONLY
// a non-sensitive banner-dismissal timestamp (no PII, no tokens, no consent-scope
// data). Auth tokens live in sessionStorage (see lib/api.js). §25(2) TTDSG storage
// requires no consent; the banner itself is purely informational.
const ACK_KEY = "sf_storage_notice_ack";

export default function ConsentBanner() {
  const [visible, setVisible] = useState(() => {
    try {
      return !window.localStorage.getItem(ACK_KEY);
    } catch (e) {
      // Storage disabled (private mode) — hide banner rather than nag every render.
      console.warn("Storage notice ack unreadable:", e);
      return false;
    }
  });

  if (!visible) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(ACK_KEY, new Date().toISOString());
    } catch (e) {
      // Storage unavailable — banner will reappear next visit, which is acceptable.
      console.warn("Could not persist storage-notice dismissal:", e);
    }
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-50 border-t-2 border-foreground bg-background"
      role="dialog"
      aria-label="Storage notice"
      data-testid="consent-banner"
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs font-mono text-muted-foreground leading-relaxed max-w-2xl">
          No tracking cookies. We use only technically necessary storage — a session
          login token and this notice's dismissal (§25(2) TTDSG). Details in our{" "}
          <Link to="/privacy" className="underline hover:text-foreground" data-testid="consent-banner-privacy-link">
            Privacy Policy
          </Link>{" "}
          &{" "}
          <Link to="/impressum" className="underline hover:text-foreground" data-testid="consent-banner-impressum-link">
            Impressum
          </Link>
          .
        </p>
        <Button
          onClick={dismiss}
          className="rounded-none h-9 px-5 bg-foreground text-background hover:bg-[hsl(var(--accent))] font-mono text-xs uppercase tracking-widest"
          data-testid="consent-banner-ok"
        >
          Got it
        </Button>
      </div>
    </div>
  );
}
